import { getWebImageUrl } from '../../../utils/image';
import { DEFAULT_FINANCIAL_PYRAMID, PYRAMID_SECTIONS } from './constants';
import _ from 'lodash';
import { parseFormattedNumber } from '../../../utils/formatNumber';

const PYRAMID_SEARCH_FIELD_MAPPINGS = [
  { suffix: 'Name', field: 'name', isNumber: false },
  { suffix: 'Value', field: 'value', isNumber: true },
  { suffix: 'MonthlyPayment', field: 'monthlyPayment', isNumber: true }
];
const EXPENSE_SEARCH_FIELDS = [
  'diet',
  'transportation',
  'rentMortgage',
  'waterElectricityGas',
  'supportParents',
  'children',
  'shopping',
  'pets',
  'domesticHelpers',
  'otherHouseholdExpenses',
  'entertainment',
  'digital',
  'membership',
  'mpf',
  'insurance',
  'tax',
  'privateCar',
  'repayment',
  'tuition',
  'otherMiscellaneousExpenses'
];

const parseSearchNumber = value => {
  const formattedValue = parseFormattedNumber(value);
  return Number.isNaN(formattedValue) ? undefined : formattedValue;
};

export const focusRemainsInEditFlow = event => {
  const nextFocusedElement = event.relatedTarget;

  return Boolean(
    nextFocusedElement &&
      (event.currentTarget.contains(nextFocusedElement) || nextFocusedElement.closest?.('[role="dialog"]'))
  );
};

export const formatEditFieldLabel = (intl, fieldLabel) =>
  intl.formatMessage({ id: 'edit-field' }, { field: fieldLabel });

export const formatPyramidData = (riskData, investmentData, savingData, protectionData, monthlyIncome) => {
  const reduceData = data =>
    (data || []).reduce(
      (accumulator, current) => {
        if (current) {
          accumulator.asset += current.value || 0;
          accumulator.monthlyPayment += current.monthlyPayment || 0;
        }
        return accumulator;
      },
      { asset: 0, monthlyPayment: 0 }
    );

  const formattedPercentage = (value, total) => (total ? (value / total) * 100 : undefined);

  const formattedRiskData = reduceData(riskData);
  const formattedInvestmentData = reduceData(investmentData);
  const formattedSavingData = reduceData(savingData);
  const formattedProtectionData = reduceData(protectionData);

  const totalAsset = formattedRiskData.asset + formattedInvestmentData.asset + formattedSavingData.asset;

  const premiumIncomePercentage = formattedPercentage(formattedProtectionData.monthlyPayment, monthlyIncome);

  return {
    totalAsset: totalAsset,
    risk: {
      asset: formattedRiskData.asset,
      monthlyPayment: formattedRiskData.monthlyPayment,
      assetPercentage: formattedPercentage(formattedRiskData.asset, totalAsset),
      monthlyPaymentPercentage: formattedPercentage(formattedRiskData.monthlyPayment, monthlyIncome)
    },
    investment: {
      asset: formattedInvestmentData.asset,
      monthlyPayment: formattedInvestmentData.monthlyPayment,
      assetPercentage: formattedPercentage(formattedInvestmentData.asset, totalAsset),
      monthlyPaymentPercentage: formattedPercentage(formattedInvestmentData.monthlyPayment, monthlyIncome)
    },
    saving: {
      asset: formattedSavingData.asset,
      monthlyPayment: formattedSavingData.monthlyPayment,
      assetPercentage: formattedPercentage(formattedSavingData.asset, totalAsset),
      monthlyPaymentPercentage: formattedPercentage(formattedSavingData.monthlyPayment, monthlyIncome)
    },
    protection: {
      asset: formattedProtectionData.asset,
      monthlyPayment: formattedProtectionData.monthlyPayment,
      premiumIncomePercentage: premiumIncomePercentage,
      monthlyPaymentPercentage: formattedPercentage(formattedProtectionData.monthlyPayment, monthlyIncome)
    }
  };
};

export const formatPremiumIncomePercentageDisplay = percentage =>
  percentage ? `${percentage ? percentage.toFixed(2) : 0}%` : '-';

export const getHandwritingImage = locale => {
  let src;
  switch (locale) {
    case 'zh-Hant-HK':
      src = 'img/financial-pyramid/triangle_frame.png';
      break;
    case 'zh':
      src = 'img/financial-pyramid/triangle_frame_simplified.png';
      break;
    default:
      src = 'img/financial-pyramid/triangle_frame_eng.png';
  }

  return getWebImageUrl(src);
};

export const getDefaultFinancialPyramidCopy = language => _.cloneDeep(DEFAULT_FINANCIAL_PYRAMID.get(language));

export const getFinancialPyramidDataFromSearch = (searchObject, baseEntry) => {
  const entry = _.cloneDeep(baseEntry || {});
  let hasEntryChanges = false;
  let hasFactsheetChanges = false;
  let monthlyIncome;
  let hasMonthlyIncome = false;
  let monthlyExpense;
  let hasMonthlyExpense = false;
  const expenses = {};
  let hasExpenses = false;

  if (Object.prototype.hasOwnProperty.call(searchObject, 'monthlyIncome')) {
    const parsedMonthlyIncome = parseSearchNumber(searchObject.monthlyIncome);
    if (parsedMonthlyIncome !== undefined) {
      monthlyIncome = parsedMonthlyIncome;
      hasMonthlyIncome = true;
      hasFactsheetChanges = true;
    }
  }

  if (Object.prototype.hasOwnProperty.call(searchObject, 'monthlyExpense')) {
    const parsedMonthlyExpense = parseSearchNumber(searchObject.monthlyExpense);
    if (parsedMonthlyExpense !== undefined) {
      monthlyExpense = parsedMonthlyExpense;
      hasMonthlyExpense = true;
      hasFactsheetChanges = true;
    }
  }

  for (const expenseField of EXPENSE_SEARCH_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(searchObject, expenseField)) {
      continue;
    }

    const parsedExpenseValue = parseSearchNumber(searchObject[expenseField]);
    if (parsedExpenseValue === undefined) {
      continue;
    }

    expenses[expenseField] = parsedExpenseValue;
    hasExpenses = true;
    hasFactsheetChanges = true;
  }

  if (Object.prototype.hasOwnProperty.call(searchObject, 'emergencyFund')) {
    const parsedEmergencyFund = parseSearchNumber(searchObject.emergencyFund);
    if (parsedEmergencyFund !== undefined) {
      entry.emergencyFund = parsedEmergencyFund;
      hasEntryChanges = true;
    }
  }

  for (const section of PYRAMID_SECTIONS) {
    const sectionRows = entry[section];
    if (!Array.isArray(sectionRows)) {
      continue;
    }

    for (let index = 0; index < sectionRows.length; index += 1) {
      for (const mapping of PYRAMID_SEARCH_FIELD_MAPPINGS) {
        const queryKey = `${section}${index}${mapping.suffix}`;
        if (!Object.prototype.hasOwnProperty.call(searchObject, queryKey)) {
          continue;
        }

        const nextValue = mapping.isNumber ? parseSearchNumber(searchObject[queryKey]) : searchObject[queryKey];
        if (nextValue === undefined) {
          continue;
        }

        const currentRow = sectionRows[index] || {};
        sectionRows[index] = { ...currentRow, [mapping.field]: nextValue };
        hasEntryChanges = true;
      }
    }
  }

  return {
    factsheetPatch: {
      monthlyIncome,
      hasMonthlyIncome,
      monthlyExpense,
      hasMonthlyExpense,
      expenses,
      hasExpenses
    },
    entryPatch: {
      entry
    },
    hasFactsheetChanges,
    hasEntryChanges,
    hasChanges: hasFactsheetChanges || hasEntryChanges
  };
};
